import { Component } from '@theme/component';

/**
 * Pre-order notice for the shared product buy box.
 *
 * Wraps the add-to-cart `<form>` (`#AddToCartForm`) and intercepts its submit
 * in the capture phase. When the product is a pre-order with a known ship date,
 * the first submit is held back and a short informational dialog is shown
 * stating the ship date. Clicking "Continue" re-runs the original submit (which
 * carries the existing inline AJAX add-to-cart logic) by setting a flag so the
 * gate lets it through.
 *
 * The buy box's own submit handler is left untouched; this only shows the notice
 * in front of it.
 *
 * @typedef {object} PreorderConfirmRefs
 * @property {HTMLDialogElement} dialog - The notice dialog element.
 *
 * @extends {Component<PreorderConfirmRefs>}
 */
class PreorderConfirm extends Component {
  /** @type {string[]} */
  requiredRefs = ['dialog'];

  /** Whether the customer has already dismissed the notice this page session. */
  #dismissed = false;

  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();
    this.#form = this.querySelector('form');
    // Capture phase so we run before the buy box's own submit handler.
    this.#form?.addEventListener('submit', this.#interceptSubmit, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#form?.removeEventListener('submit', this.#interceptSubmit, true);
  }

  /**
   * Holds back the first add-to-cart and shows the notice. Once the customer has
   * dismissed it, every later add (e.g. adding more units) goes straight through
   * without the dialog reappearing.
   * @param {Event} event
   */
  #interceptSubmit = (event) => {
    if (this.#dismissed) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#openDialog();
  };

  #openDialog = () => {
    const { dialog } = this.refs;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  };

  closeDialog = () => {
    const { dialog } = this.refs;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  /**
   * Continues past the notice: closes the dialog, then re-submits the form so
   * the existing add-to-cart flow runs normally.
   */
  confirmPreorder = () => {
    this.closeDialog();
    this.#dismissed = true;
    this.#form?.requestSubmit();
  };
}

if (!customElements.get('preorder-confirm')) {
  customElements.define('preorder-confirm', PreorderConfirm);
}
